import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type Address } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { LocationFields } from '@/components/ui/LocationFields';

/**
 * Where a buyer's things go.
 *
 * ── WHY THIS EXISTS SEPARATELY FROM CHECKOUT ────────────────────────────────
 *
 * Addresses could only be added mid-checkout, which meant the one moment a
 * buyer had to get it right was the moment they were in a hurry and holding a
 * card. Somebody moving house, or adding a parent's address, or fixing a typo
 * that has sent two deliveries to the wrong street had nowhere to go at all.
 *
 * ── AND WHY THE STATE AND LGA ARE PICKED ────────────────────────────────────
 *
 * Delivery is priced by the zone covering this address. A buyer who types
 * "Wuse" matches no zone, so they quietly pay the platform default instead of
 * their real local fee — and nothing tells them, or us. The district belongs in
 * the street line, where the rider actually reads it.
 */
export function AddressBookScreen() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['addresses'],
    queryFn: ({ signal }) => api.addresses(signal),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteAddress(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['addresses'] }),
  });

  const addresses = data ?? [];

  return (
    <>
      <AppBar back title="Your addresses" />

      <div className="mx-auto w-full max-w-[560px] px-4 pb-8 pt-4 xl:px-6">
        {/* ── WHAT WAS HERE ──────────────────────────────────────
            Three lines of grey prose explaining that the LGA decides the
            delivery price and is picked rather than typed, and that the
            district belongs in the street line.

            LocationFields ALREADY says that, under the LGA field, where
            somebody is actually choosing one — "The local government area, not
            the district. Wuse and Garki are in Abuja Municipal." So the page
            opened by explaining a field three scrolls below it, and then the
            field explained itself again.

            What replaces it is the reason to be on this screen at all, which
            was buried in the empty state's body text. */}
        {!adding ? (
          <section
            data-testid="address-hero"
            className="relative overflow-hidden rounded-[16px] bg-[linear-gradient(135deg,var(--color-blue-deep),var(--color-blue))] px-5 py-5 text-white"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_88%_-10%,rgba(255,255,255,0.18),transparent_58%)]"
            />

            <div className="relative">
              <h1 className="m-0 font-display text-[21px] font-bold leading-[1.15] tracking-[-0.025em]">
                Where we deliver
              </h1>

              <p className="m-0 mt-1.5 max-w-[42ch] text-[12.5px] leading-snug text-white/75">
                Save one and checkout is a single step, with delivery priced up front.
              </p>

              <button
                type="button"
                onClick={() => setAdding(true)}
                data-testid="add-address"
                className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-btn bg-white px-5 text-[13.5px] font-bold text-blue-deep sm:w-auto"
              >
                {addresses.length === 0 ? 'Add an address' : 'Add another'}
              </button>
            </div>
          </section>
        ) : null}

        {isLoading ? (
          <div className="mt-3 space-y-2.5">
            <Skeleton className="h-24 w-full" radius="card" />
            <Skeleton className="h-24 w-full" radius="card" />
          </div>
        ) : addresses.length === 0 && !adding ? (
          /* One line. It was a bordered panel with its own heading, its own
             sentence and a duplicate of the button directly above it. */
          <p className="mt-5 flex items-center justify-center gap-2 text-[12.5px] text-faint">
            <Icon name="pin" size={14} strokeWidth={1.9} />
            No addresses saved yet
          </p>
        ) : addresses.length > 0 ? (
          <div className={cn('space-y-2.5', adding ? 'mt-0' : 'mt-3')}>
            <h2 className="m-0 font-mono text-[10.5px] uppercase tracking-[0.12em] text-faint">
              Saved
            </h2>
            {addresses.map((address) => (
              <AddressCard
                key={address.id}
                address={address}
                onRemove={() => remove.mutate(address.id)}
                removing={remove.isPending}
              />
            ))}
          </div>
        ) : null}

        {adding ? <AddressForm onDone={() => setAdding(false)} /> : null}
      </div>
    </>
  );
}

function AddressCard({
  address,
  onRemove,
  removing,
}: {
  address: Address;
  onRemove: () => void;
  removing: boolean;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <Card className="!p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="m-0 flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
            {address.label}
            {address.is_default ? <Badge tone="brand">Default</Badge> : null}
          </p>
          <p className="m-0 mt-0.5 text-[12.5px] leading-snug text-muted">{address.line1}</p>
          <p className="m-0 mt-0.5 text-[12px] text-faint">
            {[address.lga, address.state].filter(Boolean).join(', ')}
          </p>
          <p className="m-0 mt-0.5 text-[12px] text-faint">
            {address.recipient_name} · {address.phone}
          </p>
        </div>

        <Icon name="pin" size={16} strokeWidth={1.9} className="shrink-0 text-faint" />
      </div>

      {confirming ? (
        <div className="mt-3 border-t border-line pt-3">
          {/* Said plainly. An address attached to an order in flight is how a
              rider ends up with nowhere to deliver. */}
          <p className="m-0 text-[12.5px] leading-relaxed text-ink">
            Remove this address? Orders already on their way keep the address they were placed
            with.
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            <Button size="sm" disabled={removing} onClick={onRemove}>
              {removing ? 'Removing…' : 'Remove it'}
            </Button>
            <Button size="sm" variant="grey" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="mt-2 min-h-11 bg-transparent p-0 text-[12.5px] font-bold text-muted"
        >
          Remove
        </button>
      )}
    </Card>
  );
}

function AddressForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    label: 'Home',
    recipient_name: '',
    phone: '',
    line1: '',
    // The API carries a second line; this form does not ask for one, because
    // a Nigerian street address is one line and a flat number, and an empty
    // box invites people to split it in half.
    line2: null as string | null,
    lga: '',
    // Nothing preselected: a default state is one most people never notice
    // they accepted, and an address in the wrong one is a delivery to the
    // wrong city.
    state: '',
    is_default: false,
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const create = useMutation({
    mutationFn: () => api.createAddress(form),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['addresses'] });
      onDone();
    },
  });

  const error = create.error instanceof ApiError ? create.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  const ready =
    form.recipient_name.trim() !== '' &&
    form.phone.trim() !== '' &&
    form.line1.trim() !== '' &&
    form.state !== '';

  return (
    <Card className="!p-5">
      <h2 className="m-0 font-display text-[16px] font-bold tracking-[-0.015em]">New address</h2>

      <div className="mt-4 space-y-3">
        <Field label="Name for it" value={form.label} onChange={set('label')} placeholder="Home" />

        {/* Grouped, because seven fields in one column is a wall. Who it goes
            to, then where it goes — the two questions a delivery actually
            asks. */}
        <GroupLabel>Who receives it</GroupLabel>

        <div className="grid gap-3 sm:grid-cols-2">
          {/* "Full name", not "Who receives it": the group heading directly
              above says that already, and the two stacked read as a stutter. */}
          <Field
            label="Full name"
            value={form.recipient_name}
            onChange={set('recipient_name')}
            error={fieldErrors.recipient_name?.[0]}
            placeholder="Ada Okonkwo"
          />
          <Field
            label="Phone"
            value={form.phone}
            onChange={set('phone')}
            error={fieldErrors.phone?.[0]}
            placeholder="0803 123 4567"
          />
        </div>

        <GroupLabel>Where it goes</GroupLabel>

        <Field
          label="Street and area"
          hint="Include the district — Wuse II, Lekki Phase 1."
          value={form.line1}
          onChange={set('line1')}
          error={fieldErrors.line1?.[0]}
          placeholder="12 Aminu Kano Crescent, Wuse II"
        />

        <LocationFields
          state={form.state}
          lga={form.lga}
          onState={set('state')}
          onLga={set('lga')}
          stateError={fieldErrors.state?.[0]}
          lgaError={fieldErrors.lga?.[0]}
        />

        <label className="flex items-start gap-2.5">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(event) => setForm((c) => ({ ...c, is_default: event.target.checked }))}
            className="mt-0.5"
          />
          <span className="text-[13px] leading-snug text-ink">Use this one by default</span>
        </label>
      </div>

      {error && !error.isValidation ? (
        <p role="alert" className="m-0 mt-3 text-[13px] font-medium text-danger">
          {error.message}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button disabled={!ready || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? 'Saving…' : 'Save this address'}
        </Button>
        <Button variant="grey" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}

/** A quiet divider between groups of fields. */
function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 !mt-5 border-t border-line-2 pt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
      {children}
    </p>
  );
}

function Field({
  label,
  value,
  onChange,
  hint,
  error,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12.5px] font-medium text-ink">{label}</span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className={cn(
          'min-h-12 w-full rounded-btn border bg-white px-3 text-[14px] text-ink outline-none focus:border-blue',
          error ? 'border-danger' : 'border-line',
        )}
      />
      {error ? (
        <span role="alert" className="mt-1 block text-[12px] font-medium text-danger">
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1 block text-[11.5px] leading-relaxed text-muted">{hint}</span>
      ) : null}
    </label>
  );
}
